import React from "react";
import { Form, Field } from "react-final-form";

import { useLocalization } from "../../../../../../context/LocalizeContextProvider";
import PanelUpdatedData from "../../../../../components/PanelUpdatedData";
import CardWrapperInput from "../../../../../components/CardWrapperInput";
import AsyncSelect from "../../../../../components/AsyncSelect";
import InputField from "../../../../../components/InputField";
import UploadFileField from "../../../../../components/UploadFileField";
import scrollToFirstErrorOnSubmit from "../scrollToFirstErrorOnSubmit";

export default function PharmacyAssesment({
  RenderActionButton,
  handleSubmitForm = () => undefined,
  initialValues = {},
  handleOnChange = () => undefined,
  loadOptions = () => undefined,
  isView = false,
  onEditClick = () => undefined,
}) {
  const localize = useLocalization();

  return (
    <>
      <PanelUpdatedData
        updatedAt={initialValues?.updatedAt}
        updatedBy={initialValues?.updatedBy}
        isEdit={isView}
        onClickEdit={onEditClick}
      />

      <Form
        onSubmit={handleSubmitForm}
        initialValues={initialValues}
        // Preserve dirty fields across parent re-renders (e.g. cancelling the
        // unsaved-changes modal via Batal). See skill: unsaved-changes-guard.
        keepDirtyOnReinitialize
        validate={(values) => {
          const errors = {};
          if (!values.subjective) {
            errors.subjective = localize.getText(
              "generalRequiredFieldValidation",
              { fieldName: localize.getText("subjective") }
            );
          }
          return errors;
        }}
      >
        {({ handleSubmit, submitting, values, form }) => (
          <form onSubmit={scrollToFirstErrorOnSubmit(handleSubmit, form)}>
            <div className="flex-1 d-flex flex-column gap-3">
              <Field name="allergies">
                {({ input, meta }) => (
                  <CardWrapperInput
                    label={localize.getText("alergies")}
                    icon={"fas fa-exclamation-triangle"}
                    colorIcon={"#ED8030"}
                  >
                    <AsyncSelect
                      placeholder="Select Alergies..."
                      isMulti
                      isCreatable
                      error={meta.touched && meta.error}
                      onChange={(value) => {
                        input.onChange(value);
                        handleOnChange("allergies", value);
                      }}
                      onBlur={input.onBlur}
                      disabled={isView}
                      value={values.allergies || initialValues?.allergies}
                    />
                  </CardWrapperInput>
                )}
              </Field>

              <Field name="historyOfIllness">
                {({ input, meta }) => (
                  <CardWrapperInput
                    label={localize.getText("historyOfIllness")}
                    icon={"fas fa-notes-medical"}
                    colorIcon={"#3B82F6"}
                  >
                    <InputField
                      type="textarea"
                      rows={6}
                      error={meta.touched && meta.error}
                      onChange={(value) => {
                        input.onChange(value);
                        handleOnChange("historyOfIllness", value);
                      }}
                      onBlur={input.onBlur}
                      value={
                        values.historyOfIllness ||
                        initialValues?.historyOfIllness
                      }
                      disabled={isView}
                    />
                  </CardWrapperInput>
                )}
              </Field>

              <Field name="familyHistoryOfDisease">
                {({ input, meta }) => (
                  <CardWrapperInput
                    label={localize.getText("familyHistoryOfDisease")}
                    icon={"far fa-file"}
                    colorIcon={"#09BD3C"}
                  >
                    <InputField
                      type="textarea"
                      rows={6}
                      error={meta.touched && meta.error}
                      onChange={(value) => {
                        input.onChange(value);
                        handleOnChange("familyHistoryOfDisease", value);
                      }}
                      onBlur={input.onBlur}
                      value={
                        values.familyHistoryOfDisease ||
                        initialValues?.familyHistoryOfDisease
                      }
                      disabled={isView}
                    />
                  </CardWrapperInput>
                )}
              </Field>

              <Field name="routineMedications">
                {({ input, meta }) => (
                  <CardWrapperInput
                    label={localize.getText("routineMedications")}
                    icon={"fas fa-briefcase-medical"}
                    colorIcon={"#D653C1"}
                  >
                    <InputField
                      type="textarea"
                      rows={6}
                      error={meta.touched && meta.error}
                      onChange={(value) => {
                        input.onChange(value);
                        handleOnChange("routineMedications", value);
                      }}
                      onBlur={input.onBlur}
                      value={
                        values.routineMedications ||
                        initialValues?.routineMedications
                      }
                      disabled={isView}
                    />
                  </CardWrapperInput>
                )}
              </Field>

              <Field name="subjective">
                {({ input, meta }) => (
                  <CardWrapperInput
                    label={localize.getText("subjective")}
                    icon={"fas fa-stethoscope"}
                    colorIcon={"#FC2E53"}
                  >
                    <InputField
                      type="textarea"
                      rows={6}
                      id={input.name}
                      error={meta.touched && meta.error}
                      onChange={(value) => {
                        input.onChange(value);
                        handleOnChange("subjective", value);
                      }}
                      onBlur={input.onBlur}
                      value={values.subjective ?? initialValues.subjective ?? ""}
                      disabled={isView}
                    />
                  </CardWrapperInput>
                )}
              </Field>

              <Field name="objective">
                {({ input, meta }) => (
                  <CardWrapperInput
                    label={localize.getText("objective")}
                    icon={"fas fa-stethoscope"}
                    colorIcon={"#1D4ED8"}
                  >
                    <InputField
                      type="textarea"
                      rows={6}
                      error={meta.touched && meta.error}
                      onChange={(value) => {
                        input.onChange(value);
                        handleOnChange("objective", value);
                      }}
                      onBlur={input.onBlur}
                      value={values.objective || initialValues.objective}
                      disabled={isView}
                    />
                  </CardWrapperInput>
                )}
              </Field>

              <Field name="assessment">
                {({ input, meta }) => (
                  <CardWrapperInput
                    label={localize.getText("assessment")}
                    icon={"fas fa-diagnoses"}
                    colorIcon={"#8B5CF6"}
                  >
                    <InputField
                      type="textarea"
                      rows={6}
                      error={meta.touched && meta.error}
                      onChange={(value) => {
                        input.onChange(value);
                        handleOnChange("assessment", value);
                      }}
                      onBlur={input.onBlur}
                      value={values.assessment || initialValues.assessment}
                      disabled={isView}
                    />
                  </CardWrapperInput>
                )}
              </Field>

              <Field name="plan">
                {({ input, meta }) => (
                  <CardWrapperInput
                    label={localize.getText("plan")}
                    icon={"fas fa-pills"}
                    colorIcon={"#10B981"}
                  >
                    <InputField
                      type="textarea"
                      rows={6}
                      error={meta.touched && meta.error}
                      onChange={(value) => {
                        input.onChange(value);
                        handleOnChange("plan", value);
                      }}
                      onBlur={input.onBlur}
                      value={values.plan || initialValues.plan}
                      disabled={isView}
                    />
                  </CardWrapperInput>
                )}
              </Field>

              <Field name="fileUploads">
                {({ input, meta }) => (
                  <CardWrapperInput
                    label={localize.getText("fileUpload")}
                    icon={"fas fa-paperclip"}
                    colorIcon={"#6B7280"}
                  >
                    <UploadFileField
                      error={meta.touched && meta.error}
                      onChange={(value) => {
                        input.onChange(value);
                        handleOnChange("fileUploads", value);
                      }}
                      accept="file/*"
                      value={values.fileUploads || initialValues?.fileUploads}
                      disabled={isView}
                    />
                  </CardWrapperInput>
                )}
              </Field>

              <RenderActionButton />
            </div>
          </form>
        )}
      </Form>
    </>
  );
}
